/**
 * ضغط الصور في المتصفح قبل رفعها إلى Firebase Storage.
 *
 * صور بحجم عدة ميجابايت تجعل أول فتح للتطبيق يحمّل عشرات الميجابايت قبل ظهور
 * أي صورة، وتفشل عن العرض على الأجهزة ذات الذاكرة المحدودة.
 */

export type CompressPreset = 'slider' | 'categoryIcon' | 'product'

const PRESETS: Record<CompressPreset, { maxWidth: number; quality: number }> = {
  slider: { maxWidth: 1440, quality: 0.85 },
  categoryIcon: { maxWidth: 700, quality: 0.85 },
  product: { maxWidth: 1080, quality: 0.85 },
}

/** الصور المتحركة والمتجهية تُرفع كما هي — الضغط يفقدها خصائصها. */
function shouldSkip(file: File): boolean {
  return file.type === 'image/gif' || file.type === 'image/svg+xml'
}

function loadBitmap(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file)
  }
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve(img)
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('تعذّر قراءة الصورة'))
    }
    img.src = url
  })
}

export async function compressImageForUpload(
  file: File,
  preset: CompressPreset
): Promise<File> {
  if (!file.type.startsWith('image/') || shouldSkip(file)) return file

  const { maxWidth, quality } = PRESETS[preset]

  try {
    const source = await loadBitmap(file)
    const srcWidth = 'width' in source ? source.width : 0
    const srcHeight = 'height' in source ? source.height : 0
    if (!srcWidth || !srcHeight) return file

    const scale = Math.min(1, maxWidth / srcWidth)
    const width = Math.round(srcWidth * scale)
    const height = Math.round(srcHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) return file

    // خلفية بيضاء: JPEG لا يدعم الشفافية، وبدونها تصبح المناطق الشفافة سوداء.
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, width, height)
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(source as CanvasImageSource, 0, 0, width, height)
    if ('close' in source) source.close()

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', quality)
    )
    if (!blob || blob.size >= file.size) return file

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg'
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() })
  } catch {
    return file
  }
}
